/**
 * Thin localStorage wrapper with safe fallbacks. Some browsers throw on
 * localStorage access (Safari private mode, sandboxed iframes). We treat
 * any failure as "no storage" and fall back to in-memory state.
 *
 * Named LocalStore to avoid colliding with the DOM `Storage` global.
 */

type Bag = Record<string, string>;

function safeLocalStorage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    const k = "__a_test__";
    window.localStorage.setItem(k, "1");
    window.localStorage.removeItem(k);
    return window.localStorage;
  } catch {
    return null;
  }
}

export class LocalStore {
  private prefix: string;
  private ls: Storage | null;
  private mem: Bag = {};

  constructor(prefix: string) {
    this.prefix = prefix;
    this.ls = safeLocalStorage();
  }

  private k(key: string) {
    return `${this.prefix}:${key}`;
  }

  get(key: string): string | null {
    const k = this.k(key);
    if (this.ls) {
      try {
        return this.ls.getItem(k);
      } catch {
        // fall through
      }
    }
    return this.mem[k] ?? null;
  }

  set(key: string, value: string): void {
    const k = this.k(key);
    if (this.ls) {
      try {
        this.ls.setItem(k, value);
        return;
      } catch {
        // quota or disabled — drop to memory
      }
    }
    this.mem[k] = value;
  }

  remove(key: string): void {
    const k = this.k(key);
    if (this.ls) {
      try {
        this.ls.removeItem(k);
      } catch {
        // ignore
      }
    }
    delete this.mem[k];
  }

  getJson<T>(key: string): T | null {
    const raw = this.get(key);
    if (raw == null) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  setJson(key: string, value: unknown): void {
    try {
      this.set(key, JSON.stringify(value));
    } catch {
      // circular or unserializable — silently drop
    }
  }
}
